import { Check, ChevronUp } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import styles from './ProgressTrack.module.css';

export type TrackStepStatus = 'done' | 'next' | 'todo';

export type TrackStep = {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly status: TrackStepStatus;
};

export type ProgressTrackProps = {
  title: string;
  summary: string;
  progressLabel: string;
  steps: readonly TrackStep[];
  expanded: boolean;
  toggleLabel: string;
  onToggle: () => void;
  action?: ReactNode;
  renderStep: (step: TrackStep, content: ReactNode) => ReactNode;
};

const stepContent = (step: TrackStep, position: number) => (
  <>
    <span className={styles['marker']} aria-hidden="true">
      {step.status === 'done' ? <Check size={16} strokeWidth={2.5} aria-hidden="true" /> : position}
    </span>
    <span className={styles['stepTitle']}>{step.title}</span>
    <span className={styles['stepDescription']}>{step.description}</span>
  </>
);

export const ProgressTrack = ({
  title,
  summary,
  progressLabel,
  steps,
  expanded,
  toggleLabel,
  onToggle,
  action,
  renderStep,
}: ProgressTrackProps) => {
  const titleId = useId();
  const listId = useId();
  const done = steps.filter((step) => step.status === 'done').length;
  return (
    <section className={styles['track']} aria-labelledby={titleId}>
      <div className={styles['head']}>
        <div className={styles['heading']}>
          <h2 id={titleId} className={styles['title']}>
            {title}
          </h2>
          <p className={styles['summary']}>{summary}</p>
        </div>
        <div
          className={styles['bar']}
          role="progressbar"
          aria-label={progressLabel}
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={done}
        >
          <span
            className={styles['fill']}
            style={{ inlineSize: `${String((done / Math.max(steps.length, 1)) * 100)}%` }}
          />
        </div>
        {action}
        <button
          type="button"
          className={styles['toggle']}
          aria-label={toggleLabel}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={onToggle}
        >
          <ChevronUp size={18} aria-hidden="true" />
        </button>
      </div>
      <ol id={listId} className={styles['steps']} hidden={!expanded}>
        {steps.map((step, index) => (
          <li key={step.id} className={styles['step']} data-status={step.status}>
            {renderStep(step, stepContent(step, index + 1))}
          </li>
        ))}
      </ol>
    </section>
  );
};
